import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useSession } from "../contexts/SessionContext";
import useFetch from "../hooks/useFetch";
import {
    PageHeader,
    StatusBadge,
    EmptyState,
    Skeleton,
} from "../components/shares/dashboard/DashboardWidgets";

/**
 * Sessions inside one course headline.
 *
 * The grid previously lived in a hardcoded h-[82vh] scroll container, so on a
 * laptop it produced a short inner scrollbar inside an already-scrolling page,
 * and the list had no key prop. Attendance is now a badge rather than a bare
 * coloured word, and it reads as "watched / not watched" which is what the
 * classroom_presence flag actually means for a recorded session.
 */
const ClassesList = () => {
    const location = useLocation();
    const classCat = location.state;
    const [call, setCall] = useState();
    const { classesList, setClassesList, setIsReady } = useSession();

    const fetchCategory = useFetch({
        method: "POST",
        url: `classroom/get_by_headline/`,
        noHeader: false,
        trigger: false,
        data: call,
        setter: setClassesList,
    });

    const redirect = useNavigate();

    const submit = (headline) => {
        const formData = new FormData();
        formData.append("headline", headline);
        setCall(formData);
        fetchCategory.reFetch();
    };

    useEffect(() => {
        if (classCat) {
            submit(classCat);
        } else {
            redirect("dashboard/classes");
        }
    }, [classCat]);

    const isLoading = fetchCategory.loading && !classesList;

    return (
        <div className="flex flex-col gap-xl pb-4xl">
            <PageHeader
                title={classCat || "جلسات"}
                description="جلسه‌ای که می‌خواهی ببینی را انتخاب کن"
                icon={VideocamIcon}
            />

            {isLoading ? (
                <div className="grid grid-cols-3 gap-lg max-md:grid-cols-2 max-sm:grid-cols-1">
                    {[0, 1, 2, 3, 4, 5].map((key) => (
                        <Skeleton key={key} className="h-[16rem] w-full" />
                    ))}
                </div>
            ) : !classesList?.length ? (
                <EmptyState
                    icon={VideocamIcon}
                    title="جلسه‌ای برای این درس ثبت نشده"
                    description="به‌محض انتشار جلسه‌ها، همین‌جا می‌بینی‌شان."
                    actionLabel="بازگشت به درس‌ها"
                    actionTo="/dashboard/classes"
                />
            ) : (
                <ul className="grid grid-cols-3 gap-lg max-md:grid-cols-2 max-sm:grid-cols-1">
                    {classesList.map((item, index) => (
                        <li key={item.classroom_id ?? index} style={{ "--i": index }}>
                            <Link
                                to={String(item.classroom_id)}
                                state={classCat}
                                onClick={() => setIsReady(false)}
                                className="dash-rise group flex h-full flex-col justify-between gap-lg rounded-panel border border-rule-subtle bg-surface p-xl shadow-card transition-shadow duration-200 hover:shadow-headerCard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                            >
                                <div className="flex items-start justify-between gap-sm">
                                    <span className="text-sm tabular-nums text-ink-subtle">
                                        جلسه {(index + 1).toLocaleString("fa-IR")}
                                    </span>
                                    <StatusBadge
                                        label={
                                            item.classroom_presence
                                                ? "دیده‌ای"
                                                : "دیده نشده"
                                        }
                                        tone={
                                            item.classroom_presence
                                                ? "success"
                                                : "neutral"
                                        }
                                    />
                                </div>

                                <div className="min-w-0">
                                    <h2 className="text-lg font-semibold leading-snug text-ink">
                                        {item.ClassroomName}
                                    </h2>
                                    {item.classroom_headline && (
                                        <p className="mt-2xs truncate text-sm text-ink-subtle">
                                            {item.classroom_headline}
                                        </p>
                                    )}
                                </div>

                                <span className="flex items-center gap-2xs text-sm text-primary">
                                    <PlayCircleOutlineIcon
                                        sx={{ fontSize: "1.8rem" }}
                                    />
                                    تماشای جلسه
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default ClassesList;
